<template>
    <LoadingComponent :props="loading" />
    <section class="mb-18 mt-0 sm:mt-10 sm:mb-20">
        <div class="container">
            <LoadingContentComponent :props="loadingContent" />
            <CategoryBreadcrumbComponent
                :categories="ancestorsAndSelfCategories"
                :productName="($route.query.all || $route.query.name) ? $t('label.search_results') : ''" />

            <div class="flex items-center justify-between gap-5 mt-4 mb-2 max-md:mb-8">
                <div class="flex flex-wrap items-center gap-3 max-md:flex-col max-md:items-start max-md:gap-1.5">
                    <h3 class="text-2xl font-semibold capitalize max-sm:text-lg">
                        {{ $t('label.explore_all_products') }}
                    </h3>
                    <span class="text-base font-medium capitalize max-sm:text-sm">
                        ({{
                            pagination.meta ? pagination.meta.total : 0
                        }} {{
                            categoryWiseProducts.length > 1 ? $t('label.products_found') : $t('label.product_found')
                        }})
                    </span>
                </div>
                <button @click.prevent="showTarget('filter-canvas', 'canvas-active')" type="button"
                    class="lab-line-filter md:invisible flex-shrink-0 text-lg pt-0.5 w-9 h-9 leading-9 text-center rounded-lg border border-gray-200 text-gray-500 shadow-sm md:hidden"></button>
            </div>

            <div class="flex items-start max-md:border-none">
                <div id="filter-canvas"
                    class="max-md:fixed max-md:inset-0 max-md:z-50 max-md:bg-light/50 max-md:transition-all max-md:duration-500 max-md:opacity-0 max-md:invisible">
                    <div
                        class="w-[270px] md: bg-white max-md:w-full max-md:max-w-xs max-md:transition-all max-md:duration-500 max-md:-translate-x-full">
                        <div class="max-md:h-dvh max-md:overflow-y-auto">
                            <div
                                class="md:hidden flex items-center justify-between pt-4 py-3 px-5 border-b border-slate-100">
                                <h3 class="text-base font-medium capitalize">{{ $t('label.filter_and_sorting') }}</h3>
                                <button @click.prevent="hideTarget('filter-canvas', 'canvas-active')" type="button"
                                    class="lab-line-cross text-3xl text-[#E93C3C]"></button>
                            </div>

                            <div v-if="filterVisibility.categories" class="filter-group border-b ">
                                <button :key="'eventCategoryTree'"
                                    @click.prevent="colspanHideShow($event, 'categoryTree')" type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.categories') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>
                                <div :key="'eventCategoryTreeId'" id="categoryTree"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <span class="text-xs text-gray-500 leading-4 mb-2 pr-4">{{ $t('label.click_to_expand') }}</span>
                                    <div class="w-full flex flex-col gap-4 px-6 md:px-0 mb-6">
                                        <!-- <button type="button" @click="categoryTreeOption(null)"
                                            class="text-left text-sm capitalize transition-all duration-300 hover:text-primary"
                                            :class="!$route.query.category ? 'font-semibold text-primary' : 'font-medium text-text'">
                                            {{ $t('label.products') }}
                                        </button> -->
                                        <CategoryTreeNavComponent :nodes="categoryTrees"
                                            :activeSlug="$route.query.category || ''"
                                            @select="categoryTreeOption"
                                            @toggle="recalculateCategoryTreePanelHeight" />
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.sort_by" class="filter-group border-b ">
                                <button :key="'eventSortBy'" @click.prevent="colspanHideShow($event, 'sortBy')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">{{
                                            $t('label.sort_by')
                                        }}</span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>
                                <div :key="'eventSortById'" id="sortBy"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="sortByNewest" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @click="sortByOption($event, 'newest')" v-model="productSortBy"
                                                value="latest" type="radio" id="sortByNewest" class="cs-custom-radio">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ $t('label.newest') }}
                                            </span>
                                        </label>

                                        <label for="priceLowToHigh"
                                            class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @click="sortByOption($event, 'price_low_to_high')"
                                                v-model="productSortBy" value="price_low_to_high" type="radio"
                                                id="priceLowToHigh" class="cs-custom-radio">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ $t('label.price_low_to_high') }}
                                            </span>
                                        </label>

                                        <label for="priceHighToLow"
                                            class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @click="sortByOption($event, 'price_high_to_low')"
                                                v-model="productSortBy" value="price_high_to_low" type="radio"
                                                id="priceHighToLow" class="cs-custom-radio">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ $t('label.price_high_to_low') }}
                                            </span>
                                        </label>

                                        <label for="topRated" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @click="sortByOption($event, 'top_rated')" v-model="productSortBy"
                                                value="top_rated" type="radio" id="topRated" class="cs-custom-radio">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ $t('label.top_rated') }}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.price" class="filter-group border-b ">
                                <button :key="'eventFilterPrice'"
                                    @click.prevent="colspanHideShow($event, 'filterPrice')" type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">{{
                                            $t('label.price')
                                        }}</span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>
                                <div :key="'eventFilterPriceId'" id="filterPrice"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <div class="form-row sm:p-1">
                                            <div class="form-col-12 sm:form-col-6">
                                                <input @keyup="priceOptionUpdate" v-on:keypress="onlyNumber($event)"
                                                    class="db-field-control" type="number"
                                                    v-model="productPrice.range[0]">
                                            </div>
                                            <div class="form-col-12 sm:form-col-6">
                                                <input @keyup="priceOptionUpdate" v-on:keypress="onlyNumber($event)"
                                                    class="db-field-control" type="number"
                                                    v-model="productPrice.range[1]">
                                            </div>
                                        </div>
                                        <VueSimpleRangeSlider @mouseup="priceOptionRange" @touchend="priceOptionRange"
                                            :keepJustSignificantFigures="true" popover-content-editable="false"
                                            significant-figures="1" active-bar-color="#FD8B0E" bar-color="#D9DBE9"
                                            class="p-1 w-full" :min="0" :max="maxRange" v-model="productPrice.range" />
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.brand && categoryWiseBrands.length > 0" class="filter-group border-b">
                                <button :key="'eventBrand'" @click.prevent="colspanHideShow($event, 'brand')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.brand') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventBrandId'" id="brand"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label :for="'brand_' + wiseBrand.id"
                                            v-for="wiseBrand in categoryWiseBrands"
                                            class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @click="brandOption($event, wiseBrand.id)" type="checkbox"
                                                :checked="isBrandSelected(wiseBrand.id)"
                                                :id="'brand_' + wiseBrand.id" class="cs-custom-checkbox">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ wiseBrand.name }}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.stock_state" class="filter-group border-b">
                                <button :key="'eventStockState'" @click.prevent="colspanHideShow($event, 'stockState')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('menu.stock') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventStockStateId'" id="stockState"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="stockStateAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="stockStateOption(null)" :checked="!stockState" type="radio"
                                                id="stockStateAll" name="stockState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="stockStateIn" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="stockStateOption('in_stock')" :checked="stockState === 'in_stock'" type="radio"
                                                id="stockStateIn" name="stockState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.in_stock') }}</span>
                                        </label>
                                        <label for="stockStateOut" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="stockStateOption('out_of_stock')" :checked="stockState === 'out_of_stock'" type="radio"
                                                id="stockStateOut" name="stockState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.stock_out') }}</span>
                                        </label>
                                        <label for="stockStateLow" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="stockStateOption('low_stock')" :checked="stockState === 'low_stock'" type="radio"
                                                id="stockStateLow" name="stockState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.low_stock') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.discount_state" class="filter-group border-b">
                                <button :key="'eventDiscountState'" @click.prevent="colspanHideShow($event, 'discountState')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.discount') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventDiscountStateId'" id="discountState"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="discountStateAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="discountStateOption(null)" :checked="!discountState" type="radio"
                                                id="discountStateAll" name="discountState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="discountStateWith" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="discountStateOption('with_discount')" :checked="discountState === 'with_discount'" type="radio"
                                                id="discountStateWith" name="discountState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.with_discount') }}</span>
                                        </label>
                                        <label for="discountStateWithout" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="discountStateOption('without_discount')" :checked="discountState === 'without_discount'" type="radio"
                                                id="discountStateWithout" name="discountState" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.without_discount') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.rating_min" class="filter-group border-b">
                                <button :key="'eventRatingMin'" @click.prevent="colspanHideShow($event, 'ratingMin')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.top_rated') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventRatingMinId'" id="ratingMin"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="minRatingAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="minRatingOption(null)" :checked="!minRating" type="radio"
                                                id="minRatingAll" name="minRating" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="minRating4" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="minRatingOption(4)" :checked="Number(minRating) === 4" type="radio"
                                                id="minRating4" name="minRating" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.rating_4_or_more') }}</span>
                                        </label>
                                        <label for="minRating3" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="minRatingOption(3)" :checked="Number(minRating) === 3" type="radio"
                                                id="minRating3" name="minRating" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.rating_3_or_more') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.tags && availableTags.length > 0" class="filter-group border-b">
                                <button :key="'eventTagCloud'" @click.prevent="colspanHideShow($event, 'tagCloud')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.tags') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventTagCloudId'" id="tagCloud"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-wrap gap-2 px-6 md:px-0">
                                        <button v-for="tag in availableTags" :key="'tag_' + tag.id" type="button"
                                            @click="tagOption(tag.name)"
                                            class="px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-300"
                                            :class="selectedTagNames.includes(tag.name) ? 'bg-primary text-white border-primary' : 'bg-white text-text border-slate-300 hover:border-primary hover:text-primary'">
                                            {{ tag.name }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.custom_fields && Object.keys(customFieldOptions).length > 0" class="filter-group border-b">
                                <button :key="'eventCustomFields'" @click.prevent="colspanHideShow($event, 'customFields')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.custom_fields') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventCustomFieldsId'" id="customFields"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <div v-for="(options, fieldKey) in customFieldOptions" :key="'custom_field_' + fieldKey" class="flex flex-col gap-2">
                                            <h4 class="text-sm font-semibold text-text capitalize">
                                                {{ options.length ? (options[0].label || fieldKey) : fieldKey }}
                                            </h4>
                                            <label
                                                v-for="option in options"
                                                :key="option.id"
                                                :for="'custom_field_' + option.id"
                                                class="flex items-center gap-1.5 cursor-pointer group">
                                                <input type="checkbox"
                                                    :id="'custom_field_' + option.id"
                                                    :checked="isCustomFieldSelected(option.key, option.value)"
                                                    @click="customFieldOption($event, option.key, option.value)"
                                                    class="cs-custom-checkbox">
                                                <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                    {{ option.value }}
                                                    <small class="text-slate-400">({{ option.count }})</small>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.trend_mode" class="filter-group border-b">
                                <button :key="'eventTrendMode'" @click.prevent="colspanHideShow($event, 'trendMode')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.trend') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventTrendModeId'" id="trendMode"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="trendModeAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="trendModeOption(null)" :checked="!trendMode" type="radio"
                                                id="trendModeAll" name="trendMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="trendModePopular" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="trendModeOption('popular')" :checked="trendMode === 'popular'" type="radio"
                                                id="trendModePopular" name="trendMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.most_popular') }}</span>
                                        </label>
                                        <label for="trendModeRecent" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="trendModeOption('recent')" :checked="trendMode === 'recent'" type="radio"
                                                id="trendModeRecent" name="trendMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.newest') }}</span>
                                        </label>
                                        <label for="trendModeFlashSale" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="trendModeOption('flash_sale')" :checked="trendMode === 'flash_sale'" type="radio"
                                                id="trendModeFlashSale" name="trendMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.flash_sale') }}</span>
                                        </label>
                                        <label for="trendModeBestRated" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="trendModeOption('best_rated')" :checked="trendMode === 'best_rated'" type="radio"
                                                id="trendModeBestRated" name="trendMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.top_rated') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.shipping_cost_mode" class="filter-group border-b">
                                <button :key="'eventShippingCostMode'" @click.prevent="colspanHideShow($event, 'shippingCostMode')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.shipping_cost') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventShippingCostModeId'" id="shippingCostMode"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="shippingCostAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="shippingCostModeOption(null)" :checked="!shippingCostMode" type="radio"
                                                id="shippingCostAll" name="shippingCostMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="shippingCostFree" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="shippingCostModeOption('free')" :checked="shippingCostMode === 'free'" type="radio"
                                                id="shippingCostFree" name="shippingCostMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.free_shipping') }}</span>
                                        </label>
                                        <label for="shippingCostPaid" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="shippingCostModeOption('paid')" :checked="shippingCostMode === 'paid'" type="radio"
                                                id="shippingCostPaid" name="shippingCostMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.paid_shipping') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.refundable_mode" class="filter-group border-b">
                                <button :key="'eventRefundableMode'" @click.prevent="colspanHideShow($event, 'refundableMode')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.refundable') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventRefundableModeId'" id="refundableMode"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="refundableAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="refundableModeOption(null)" :checked="!refundableMode" type="radio"
                                                id="refundableAll" name="refundableMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="refundableOnly" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="refundableModeOption('refundable_only')" :checked="refundableMode === 'refundable_only'" type="radio"
                                                id="refundableOnly" name="refundableMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.only_refundable') }}</span>
                                        </label>
                                        <label for="nonRefundableOnly" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="refundableModeOption('non_refundable_only')" :checked="refundableMode === 'non_refundable_only'" type="radio"
                                                id="nonRefundableOnly" name="refundableMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">No reembolsables</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.wholesale_mode" class="filter-group " :class="filterVisibility.variations && Object.keys(categoryWiseVariations).length > 0 ? 'border-b' : ''">
                                <button :key="'eventWholesaleMode'" @click.prevent="colspanHideShow($event, 'wholesaleMode')"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ $t('label.wholesale') }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'eventWholesaleModeId'" id="wholesaleMode"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label for="wholesaleAll" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="wholesaleModeOption(null)" :checked="!wholesaleMode" type="radio"
                                                id="wholesaleAll" name="wholesaleMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.all') }}</span>
                                        </label>
                                        <label for="wholesaleOnly" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="wholesaleModeOption('wholesale_only')" :checked="wholesaleMode === 'wholesale_only'" type="radio"
                                                id="wholesaleOnly" name="wholesaleMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.only_wholesale') }}</span>
                                        </label>
                                        <label for="retailOnly" class="flex items-center gap-1.5 cursor-pointer group">
                                            <input @change="wholesaleModeOption('retail_only')" :checked="wholesaleMode === 'retail_only'" type="radio"
                                                id="retailOnly" name="wholesaleMode" class="cs-custom-radio">
                                            <span class="font-medium text-sm capitalize transition-all duration-500 group-hover:text-primary">{{ $t('label.only_retail') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filterVisibility.variations && Object.keys(categoryWiseVariations).length > 0"
                                v-for="(categoryWiseVariation, categoryWiseVariationKey) in categoryWiseVariations"
                                class="filter-group ">
                                <button :key="'variation_' + categoryWiseVariationKey"
                                    @click.prevent="colspanHideShow($event, 'variation_' + categoryWiseVariationKey)"
                                    type="button"
                                    class="filter-btn active group flex items-center justify-between w-full pt-5 pb-4 px-4 ltr:md:pl-0 rtl:pr-0">
                                    <span
                                        class="text-md font-medium capitalize transition-all duration-500 group-hover:text-primary">
                                        {{ underscoreToSpace(categoryWiseVariationKey) }}
                                    </span>
                                    <i
                                        class="lab-line-chevron-down text-base font-semibold transition-all duration-500 group-hover:text-primary"></i>
                                </button>

                                <div :key="'variation_' + categoryWiseVariationKey + 'Id'"
                                    :id="'variation_' + categoryWiseVariationKey"
                                    class="h-auto overflow-hidden transition-all duration-500">
                                    <div class="w-full mb-6 flex flex-col gap-4 px-6 md:px-0">
                                        <label
                                            :for="variation.product_attribute_id + '_' + variation.product_attribute_option_id"
                                            v-for="variation in categoryWiseVariation"
                                            class="flex items-center gap-1.5 cursor-pointer group">
                                            <input type="checkbox"
                                                @click="variationOption($event, variation.product_attribute_id, variation.product_attribute_option_id)"
                                                :checked="isVariationSelected(variation.product_attribute_id, variation.product_attribute_option_id)"
                                                :id="variation.product_attribute_id + '_' + variation.product_attribute_option_id"
                                                class="cs-custom-checkbox">
                                            <span
                                                class="font-medium text-sm m:text-sm capitalize transition-all duration-500 group-hover:text-primary">
                                                {{ variation.attribute_option_name }}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full p-6 max-md:p-0">
                    <div class="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        <LoadingContentComponent :props="loadingProducts" />
                        <ProductListComponent v-if="categoryWiseProducts.length > 0" :products="categoryWiseProducts" />
                    </div>

                    <PaginationComponent @pagination-change-page="products" :data="pagination" :limit="1"
                        :keep-length="false" />
                </div>
            </div>
            <ProductCategoryPrefooterComponent />
        </div>
    </section>
</template>


<script>

import axios from 'axios';
import targetService from "../../../services/targetService";
import ProductListComponent from "../components/ProductListComponent";
import StatusEnum from "../../../enums/modules/statusEnum";
import appService from "../../../services/appService";
import VueSimpleRangeSlider from "vue-simple-range-slider";
import "vue-simple-range-slider/css";
import LoadingComponent from "../components/LoadingComponent";
import PaginationComponent from "../components/PaginationComponent";
import LoadingContentComponent from "../components/LoadingContentComponent.vue";
import CategoryBreadcrumbComponent from "../components/CategoryBreadcrumbComponent.vue";
import CategoryTreeNavComponent from "../components/CategoryTreeNavComponent.vue";
import ProductCategoryPrefooterComponent from "./ProductCategoryPrefooterComponent.vue";

export default {
    name: "ProductComponent",
    components: {
        CategoryBreadcrumbComponent,
        CategoryTreeNavComponent,
        ProductCategoryPrefooterComponent,
        LoadingContentComponent,
        LoadingComponent,
        ProductListComponent,
        VueSimpleRangeSlider,
        PaginationComponent
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            loadingContent: {
                isActive: false
            },
            loadingProducts: {
                isActive: false
            },
            productSortBy: null,
            productBrands: [],
            productVariations: [],
            customFieldOptions: {},
            selectedCustomFields: [],
            availableTags: [],
            selectedTagNames: [],
            stockState: null,
            discountState: null,
            minRating: null,
            trendMode: null,
            shippingCostMode: null,
            refundableMode: null,
            wholesaleMode: null,
            filterVisibility: {
                categories: true,
                sort_by: true,
                price: true,
                brand: true,
                stock_state: true,
                discount_state: true,
                rating_min: true,
                tags: true,
                custom_fields: true,
                trend_mode: true,
                shipping_cost_mode: true,
                refundable_mode: true,
                wholesale_mode: true,
                variations: true,
            },
            productPrice: {
                range: [0, 0]
            },
            panelTransitionHandlers: {},
            panelAnimationFrames: {},
            maxRange: 0,
            productSearchForm: {
                all: null,
                name: null,
                brand: [],
                variation: [],
                min_price: null,
                max_price: null,
                status: StatusEnum.ACTIVE,
                category: null,
                sort_by: null,
                stock_state: null,
                discount_state: null,
                min_rating: null,
                tag_names: null,
                custom_fields: null,
                trend_mode: null,
                shipping_cost_mode: null,
                refundable_mode: null,
                wholesale_mode: null,
                page: 1,
            }
        }
    },
    computed: {
        pagination: function () {
            return this.$store.getters["frontendProduct/categoryWiseProductPagination"];
        },
        ancestorsAndSelfCategories: function () {
            return this.$store.getters["frontendProductCategory/ancestorsAndSelf"];
        },
        categoryWiseProducts: function () {
            return this.$store.getters["frontendProduct/categoryWiseProducts"];
        },
        categoryWiseBrands: function () {
            return this.$store.getters["frontendProduct/categoryWiseBrands"];
        },
        categoryWiseVariations: function () {
            return this.$store.getters["frontendProduct/categoryWiseVariations"];
        },
        categoryTrees: function () {
            return this.$store.getters["frontendProductCategory/trees"];
        }
    },
    async mounted() {
        this.fetchCategoryTree();
        await this.fetchSearchFilters();
        this.ancestorsAndSelf();
    },
    methods: {
        onlyNumber: function (e) {
            return appService.onlyNumber(e);
        },
        underscoreToSpace: function (s) {
            return appService.underscoreToSpace(s);
        },
        colspanHideShow: function (event, id) {
            const panel = document.querySelector(`#${id}`);
            const button = event.currentTarget;

            if (!panel || !button) {
                return;
            }

            const isOpen = button.classList.contains('active');
            this.setPanelOpenState(id, !isOpen);
        },
        showTarget: function (id, cClass) {
            targetService.showTarget(id, cClass);
        },
        hideTarget: function (id, cClass) {
            targetService.hideTarget(id, cClass);
        },
        fetchCategoryTree: function () {
            this.$store.dispatch("frontendProductCategory/trees").catch(() => {
            });
        },
        normalizeBoolean: function (value, fallback = true) {
            if (typeof value === 'boolean') {
                return value;
            }

            if (value === null || typeof value === 'undefined') {
                return fallback;
            }

            return ['1', 'true', 'on', 'yes'].includes(String(value).trim().toLowerCase());
        },
        applySearchFilterVisibility: function (items = {}) {
            this.filterVisibility = {
                ...this.filterVisibility,
                categories: this.normalizeBoolean(items.categories, this.filterVisibility.categories),
                sort_by: this.normalizeBoolean(items.sort_by, this.filterVisibility.sort_by),
                price: this.normalizeBoolean(items.price, this.filterVisibility.price),
                brand: this.normalizeBoolean(items.brand, this.filterVisibility.brand),
                stock_state: this.normalizeBoolean(items.stock_state, this.filterVisibility.stock_state),
                discount_state: this.normalizeBoolean(items.discount_state, this.filterVisibility.discount_state),
                rating_min: this.normalizeBoolean(items.rating_min, this.filterVisibility.rating_min),
                tags: this.normalizeBoolean(items.tags, this.filterVisibility.tags),
                custom_fields: this.normalizeBoolean(items.custom_fields, this.filterVisibility.custom_fields),
                trend_mode: this.normalizeBoolean(items.trend_mode, this.filterVisibility.trend_mode),
                shipping_cost_mode: this.normalizeBoolean(items.shipping_cost_mode, this.filterVisibility.shipping_cost_mode),
                refundable_mode: this.normalizeBoolean(items.refundable_mode, this.filterVisibility.refundable_mode),
                wholesale_mode: this.normalizeBoolean(items.wholesale_mode, this.filterVisibility.wholesale_mode),
                variations: this.normalizeBoolean(items.variations, this.filterVisibility.variations),
            };
        },
        fetchSearchFilters: async function () {
            try {
                const res = await axios.get('frontend/setting/search-filters');
                console.log("Search filters visibility loaded successfully:", res?.data?.data);
                this.applySearchFilterVisibility(res?.data?.data || {});
            } catch (e) {
                console.error("Error fetching search filters in ProductComponent:", e);
            }
        },
        categoryTreeOption: function (categorySlug) {
            this.productSearchForm.category = categorySlug || null;
            this.syncFiltersToQuery(1);
        },
        buildFilterQuery: function (page = 1) {
            const query = {};

            if (this.filterVisibility.categories && this.productSearchForm.category) query.category = this.productSearchForm.category;
            if (this.productSearchForm.all) query.all = this.productSearchForm.all;
            if (this.productSearchForm.name) query.name = this.productSearchForm.name;
            if (this.filterVisibility.sort_by && this.productSearchForm.sort_by) query.sort_by = this.productSearchForm.sort_by;

            if (this.filterVisibility.brand && Array.isArray(this.productBrands) && this.productBrands.length) {
                query.brand = this.productBrands.map((brand) => String(brand));
            }

            if (this.filterVisibility.variations && Array.isArray(this.productVariations) && this.productVariations.length) {
                query.variation = this.productVariations.map((variation) => `${variation.attribute}:${variation.option}`);
            }

            if (this.filterVisibility.tags && Array.isArray(this.selectedTagNames) && this.selectedTagNames.length) {
                query.tag_names = this.selectedTagNames.map((tag) => String(tag));
            }

            if (this.filterVisibility.custom_fields && Array.isArray(this.selectedCustomFields) && this.selectedCustomFields.length) {
                query.custom_fields = this.selectedCustomFields.map((item) => `${item.key}:${item.value}`);
            }

            if (this.filterVisibility.stock_state && this.stockState) query.stock_state = this.stockState;
            if (this.filterVisibility.discount_state && this.discountState) query.discount_state = this.discountState;
            if (this.filterVisibility.rating_min && this.minRating !== null && this.minRating !== '') query.min_rating = String(this.minRating);
            if (this.filterVisibility.trend_mode && this.trendMode) query.trend_mode = this.trendMode;
            if (this.filterVisibility.shipping_cost_mode && this.shippingCostMode) query.shipping_cost_mode = this.shippingCostMode;
            if (this.filterVisibility.refundable_mode && this.refundableMode) query.refundable_mode = this.refundableMode;
            if (this.filterVisibility.wholesale_mode && this.wholesaleMode) query.wholesale_mode = this.wholesaleMode;

            if (this.filterVisibility.price && this.productSearchForm.min_price !== null && this.productSearchForm.min_price !== '') {
                query.min_price = String(this.productSearchForm.min_price);
            }

            if (this.filterVisibility.price && this.productSearchForm.max_price !== null && this.productSearchForm.max_price !== '') {
                query.max_price = String(this.productSearchForm.max_price);
            }

            if (page > 1) {
                query.page = String(page);
            }

            return query;
        },
        normalizeQueryForCompare: function (query) {
            const normalized = {};
            const source = query || {};

            Object.keys(source).sort().forEach((key) => {
                const value = source[key];

                if (value === null || typeof value === 'undefined' || value === '') {
                    return;
                }

                if (Array.isArray(value)) {
                    const normalizedArray = value.map((item) => String(item)).filter((item) => item !== '').sort();
                    if (normalizedArray.length) {
                        normalized[key] = normalizedArray;
                    }
                    return;
                }

                normalized[key] = [String(value)];
            });

            return normalized;
        },
        isSameQuery: function (firstQuery, secondQuery) {
            return JSON.stringify(this.normalizeQueryForCompare(firstQuery)) === JSON.stringify(this.normalizeQueryForCompare(secondQuery));
        },
        syncFiltersToQuery: function (page = 1) {
            const query = this.buildFilterQuery(page);

            if (this.isSameQuery(this.$route.query, query)) {
                return;
            }

            this.$router.replace({
                name: "frontend.product",
                query,
            }).catch(() => {
            });
        },
        clearPanelPendingState: function (panelId, panel = null) {
            const targetPanel = panel || document.querySelector(`#${panelId}`);

            if (!targetPanel) {
                return;
            }

            if (this.panelTransitionHandlers[panelId]) {
                targetPanel.removeEventListener('transitionend', this.panelTransitionHandlers[panelId]);
                delete this.panelTransitionHandlers[panelId];
            }

            if (this.panelAnimationFrames[panelId]) {
                window.cancelAnimationFrame(this.panelAnimationFrames[panelId]);
                delete this.panelAnimationFrames[panelId];
            }
        },
        setPanelOpenState: function (panelId, shouldOpen) {
            const panel = document.querySelector(`#${panelId}`);
            if (!panel) {
                return;
            }

            this.clearPanelPendingState(panelId, panel);

            const button = panel.previousElementSibling;
            if (button) {
                if (shouldOpen) {
                    button.classList.add('active');
                } else {
                    button.classList.remove('active');
                }
            }

            if (shouldOpen) {
                panel.style.height = `${panel.scrollHeight}px`;

                const setAutoHeight = () => {
                    this.panelAnimationFrames[panelId] = window.requestAnimationFrame(() => {
                        panel.style.height = 'auto';
                        this.clearPanelPendingState(panelId, panel);
                    });
                };

                this.panelTransitionHandlers[panelId] = setAutoHeight;
                panel.addEventListener('transitionend', setAutoHeight, { once: true });

                return;
            }

            if (panel.style.height === 'auto') {
                panel.style.height = `${panel.scrollHeight}px`;
                this.panelAnimationFrames[panelId] = window.requestAnimationFrame(() => {
                    panel.style.height = '0px';
                    delete this.panelAnimationFrames[panelId];
                });
                return;
            }

            panel.style.height = '0px';
        },
        recalculateCategoryTreePanelHeight: function () {
            const panel = document.querySelector('#categoryTree');
            if (!panel) {
                return;
            }

            const button = panel.previousElementSibling;
            const isOpen = button && button.classList.contains('active');
            if (!isOpen) {
                return;
            }

            this.clearPanelPendingState('categoryTree', panel);

            panel.style.height = `${panel.scrollHeight}px`;
            this.panelAnimationFrames.categoryTree = window.requestAnimationFrame(() => {
                panel.style.height = 'auto';
                delete this.panelAnimationFrames.categoryTree;
            });
        },
        parseArrayQuery: function (value) {
            if (typeof value === 'undefined' || value === null || value === '') {
                return [];
            }

            if (Array.isArray(value)) {
                return value.map((item) => String(item));
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed === '') {
                    return [];
                }

                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        return Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
                    } catch (e) {
                    }
                }

                if (trimmed.includes(',')) {
                    return trimmed.split(',').map((item) => item.trim()).filter((item) => item !== '');
                }

                return [trimmed];
            }

            return [String(value)];
        },
        parseVariationsQuery: function (value) {
            if (typeof value === 'undefined' || value === null || value === '') {
                return [];
            }

            if (Array.isArray(value)) {
                return value.map((item) => {
                    if (typeof item === 'object' && item !== null && typeof item.attribute !== 'undefined' && typeof item.option !== 'undefined') {
                        return {
                            attribute: String(item.attribute),
                            option: String(item.option),
                        };
                    }

                    const [attribute, option] = String(item).split(':');
                    if (typeof attribute === 'undefined' || typeof option === 'undefined') {
                        return null;
                    }

                    return {
                        attribute: String(attribute),
                        option: String(option),
                    };
                }).filter((item) => item !== null);
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();

                if (trimmed.includes(':') && !trimmed.startsWith('[')) {
                    const [attribute, option] = trimmed.split(':');
                    if (attribute && option) {
                        return [{
                            attribute: String(attribute),
                            option: String(option),
                        }];
                    }
                }

                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) {
                        return this.parseVariationsQuery(parsed);
                    }
                } catch (e) {
                }
            }

            return [];
        },
        parseCustomFieldsQuery: function (value) {
            const rows = this.parseArrayQuery(value);
            if (!rows.length) {
                return [];
            }

            return rows.map((row) => {
                if (typeof row === 'object' && row !== null && typeof row.key !== 'undefined' && typeof row.value !== 'undefined') {
                    return {
                        key: String(row.key),
                        value: String(row.value),
                    };
                }

                const raw = String(row);
                const separatorIndex = raw.indexOf(':');
                if (separatorIndex === -1) {
                    return null;
                }

                const key = raw.substring(0, separatorIndex).trim();
                const fieldValue = raw.substring(separatorIndex + 1).trim();
                if (!key || !fieldValue) {
                    return null;
                }

                return {
                    key,
                    value: fieldValue,
                };
            }).filter((item) => item !== null);
        },
        isBrandSelected: function (brandId) {
            return this.productBrands.some((brand) => String(brand) === String(brandId));
        },
        isVariationSelected: function (attribute, option) {
            return this.productVariations.some((variation) => String(variation.attribute) === String(attribute) && String(variation.option) === String(option));
        },
        isCustomFieldSelected: function (key, value) {
            return this.selectedCustomFields.some((item) => String(item.key) === String(key) && String(item.value) === String(value));
        },
        isPriceFilterActive: function () {
            if (this.productSearchForm.min_price === null || this.productSearchForm.max_price === null) {
                return false;
            }

            return Number(this.productSearchForm.min_price) > 0 || Number(this.productSearchForm.max_price) < Number(this.maxRange);
        },
        initializeFilterPanels: function () {
            const variationPanelIds = Object.keys(this.categoryWiseVariations).map((key) => `variation_${key}`);
            const panelIds = ['categoryTree', 'sortBy', 'filterPrice', 'brand', 'stockState', 'discountState', 'ratingMin', 'tagCloud', 'customFields', 'trendMode', 'shippingCostMode', 'refundableMode', 'wholesaleMode', ...variationPanelIds];

            // Collapse all by default.
            panelIds.forEach((id) => this.setPanelOpenState(id, false));

            // Open only active filters.
            this.setPanelOpenState('categoryTree', !!this.$route.query.category);
            this.setPanelOpenState('sortBy', !!this.productSearchForm.sort_by);
            this.setPanelOpenState('filterPrice', this.isPriceFilterActive());
            this.setPanelOpenState('brand', Array.isArray(this.productBrands) && this.productBrands.length > 0);
            this.setPanelOpenState('stockState', !!this.stockState);
            this.setPanelOpenState('discountState', !!this.discountState);
            this.setPanelOpenState('ratingMin', this.minRating !== null && this.minRating !== '');
            this.setPanelOpenState('tagCloud', Array.isArray(this.selectedTagNames) && this.selectedTagNames.length > 0);
            this.setPanelOpenState('customFields', Array.isArray(this.selectedCustomFields) && this.selectedCustomFields.length > 0);
            this.setPanelOpenState('trendMode', !!this.trendMode);
            this.setPanelOpenState('shippingCostMode', !!this.shippingCostMode);
            this.setPanelOpenState('refundableMode', !!this.refundableMode);
            this.setPanelOpenState('wholesaleMode', !!this.wholesaleMode);

            const hasVariationFilter = Array.isArray(this.productVariations) && this.productVariations.length > 0;
            if (hasVariationFilter) {
                variationPanelIds.forEach((id) => this.setPanelOpenState(id, true));
            }
        },
        ancestorsAndSelf: function () {
            const query = this.$route.query || {};

            if (this.filterVisibility.categories && typeof this.$route.query.category !== "undefined" && this.$route.query.category !== "") {
                this.loading.isActive = true;
                this.productSearchForm.category = this.$route.query.category;
                this.$store.dispatch("frontendProductCategory/ancestorsAndSelf", this.$route.query.category).then(res => {
                    this.loading.isActive = false;
                }).catch((err) => {
                    this.loading.isActive = false;
                });
            } else {
                this.productSearchForm.category = null;
            }

            if (typeof this.$route.query.all !== "undefined" && this.$route.query.all !== "") {
                this.productSearchForm.all = this.$route.query.all;
                this.productSearchForm.name = null;
            } else {
                if (typeof this.$route.query.name !== "undefined" && this.$route.query.name !== "") {
                    this.productSearchForm.name = this.$route.query.name;
                } else {
                    this.productSearchForm.name = null;
                }
            }

            this.productBrands = this.parseArrayQuery(query.brand);
            this.productSearchForm.brand = this.productBrands.length ? JSON.stringify(this.productBrands) : [];
            if (!this.filterVisibility.brand) {
                this.productBrands = [];
                this.productSearchForm.brand = [];
            }

            this.productVariations = this.parseVariationsQuery(query.variation);
            this.productSearchForm.variation = this.productVariations.length ? JSON.stringify(this.productVariations) : [];
            if (!this.filterVisibility.variations) {
                this.productVariations = [];
                this.productSearchForm.variation = [];
            }

            this.selectedTagNames = this.parseArrayQuery(query.tag_names);
            if (!this.filterVisibility.tags) {
                this.selectedTagNames = [];
            }

            this.selectedCustomFields = this.parseCustomFieldsQuery(query.custom_fields);
            if (!this.filterVisibility.custom_fields) {
                this.selectedCustomFields = [];
            }

            this.stockState = (typeof query.stock_state !== "undefined" && query.stock_state !== "") ? query.stock_state : null;
            this.discountState = (typeof query.discount_state !== "undefined" && query.discount_state !== "") ? query.discount_state : null;
            this.minRating = (typeof query.min_rating !== "undefined" && query.min_rating !== "") ? Number(query.min_rating) : null;
            this.trendMode = (typeof query.trend_mode !== "undefined" && query.trend_mode !== "") ? query.trend_mode : null;
            this.shippingCostMode = (typeof query.shipping_cost_mode !== "undefined" && query.shipping_cost_mode !== "") ? query.shipping_cost_mode : null;
            this.refundableMode = (typeof query.refundable_mode !== "undefined" && query.refundable_mode !== "") ? query.refundable_mode : null;
            this.wholesaleMode = (typeof query.wholesale_mode !== "undefined" && query.wholesale_mode !== "") ? query.wholesale_mode : null;
            if (!this.filterVisibility.stock_state) this.stockState = null;
            if (!this.filterVisibility.discount_state) this.discountState = null;
            if (!this.filterVisibility.rating_min) this.minRating = null;
            if (!this.filterVisibility.trend_mode) this.trendMode = null;
            if (!this.filterVisibility.shipping_cost_mode) this.shippingCostMode = null;
            if (!this.filterVisibility.refundable_mode) this.refundableMode = null;
            if (!this.filterVisibility.wholesale_mode) this.wholesaleMode = null;

            this.productSearchForm.min_price = (typeof query.min_price !== "undefined" && query.min_price !== "") ? Number(query.min_price) : null;
            this.productSearchForm.max_price = (typeof query.max_price !== "undefined" && query.max_price !== "") ? Number(query.max_price) : null;
            if (!this.filterVisibility.price) {
                this.productSearchForm.min_price = null;
                this.productSearchForm.max_price = null;
            }

            if (this.filterVisibility.sort_by && typeof this.$route.query.sort_by !== "undefined" && this.$route.query.sort_by !== "") {
                this.productSearchForm.sort_by = this.$route.query.sort_by;
                this.productSortBy = this.$route.query.sort_by;
            } else {
                this.productSearchForm.sort_by = null;
                this.productSortBy = null;
            }

            this.productSearchForm.stock_state = this.stockState;
            this.productSearchForm.discount_state = this.discountState;
            this.productSearchForm.min_rating = this.minRating;
            this.productSearchForm.tag_names = this.selectedTagNames.length ? JSON.stringify(this.selectedTagNames) : null;
            this.productSearchForm.custom_fields = this.selectedCustomFields.length ? JSON.stringify(this.selectedCustomFields) : null;
            this.productSearchForm.trend_mode = this.trendMode;
            this.productSearchForm.shipping_cost_mode = this.shippingCostMode;
            this.productSearchForm.refundable_mode = this.refundableMode;
            this.productSearchForm.wholesale_mode = this.wholesaleMode;
            this.productSearchForm.page = (typeof query.page !== "undefined" && query.page !== "") ? Number(query.page) : 1;

            this.loading.isActive = true;
            this.$store.dispatch("frontendProduct/categoryWiseProducts", this.productSearchForm).then(res => {
                this.maxRange = res.data.data.max_price;
                const minPrice = this.productSearchForm.min_price !== null ? Number(this.productSearchForm.min_price) : 0;
                const maxPrice = this.productSearchForm.max_price !== null ? Number(this.productSearchForm.max_price) : this.maxRange;
                this.productPrice.range = [Math.max(0, minPrice), Math.min(this.maxRange, maxPrice)];
                this.availableTags = res.data.data.tags || [];
                this.customFieldOptions = res?.data?.data?.custom_fields || {};
                this.loading.isActive = false;
                this.$nextTick(() => {
                    this.initializeFilterPanels();
                });
            }).catch((err) => {
                this.customFieldOptions = {};
                this.loading.isActive = false;
                this.$nextTick(() => {
                    this.initializeFilterPanels();
                });
            });
        },
        async products(page = 1) {
            this.productSearchForm.page = page;
            this.syncFiltersToQuery(page);
        },
        sortByOption: function (event, sortBy) {
            this.productSearchForm.sort_by = sortBy;
            this.products(1);
        },
        priceOptionRange() {
            this.productSearchForm.min_price = this.productPrice.range[0];
            this.productSearchForm.max_price = this.productPrice.range[1];
            this.products(1);
        },
        priceOptionUpdate() {
            this.productPrice.range = [this.productPrice.range[0], this.productPrice.range[1]];
            this.productSearchForm.min_price = this.productPrice.range[0];
            this.productSearchForm.max_price = this.productPrice.range[1];
            this.products(1);
        },
        brandOption: function (event, brand) {
            const brandValue = String(brand);

            if (event.target.checked) {
                if (!this.productBrands.some((item) => String(item) === brandValue)) {
                    this.productBrands.push(brandValue);
                }
            } else {
                for (let i = 0; i < this.productBrands.length; i++) {
                    if (String(this.productBrands[i]) === brandValue) {
                        this.productBrands.splice(i, 1);
                        break;
                    }
                }
            }
            this.productSearchForm.brand = JSON.stringify(this.productBrands);
            this.products(1);
        },
        variationOption: function (event, attribute, option) {
            const normalizedAttribute = String(attribute);
            const normalizedOption = String(option);

            if (event.target.checked) {
                const exists = this.productVariations.some((variation) => String(variation.attribute) === normalizedAttribute && String(variation.option) === normalizedOption);
                if (!exists) {
                    this.productVariations.push({
                        attribute: normalizedAttribute,
                        option: normalizedOption
                    });
                }
            } else {
                for (let i = 0; i < this.productVariations.length; i++) {
                    if (String(this.productVariations[i].attribute) === normalizedAttribute && String(this.productVariations[i].option) === normalizedOption) {
                        this.productVariations.splice(i, 1);
                        break;
                    }
                }
            }
            this.productSearchForm.variation = JSON.stringify(this.productVariations);
            this.products(1);
        },
        stockStateOption: function (value) {
            this.stockState = value;
            this.productSearchForm.stock_state = value;
            this.products(1);
        },
        discountStateOption: function (value) {
            this.discountState = value;
            this.productSearchForm.discount_state = value;
            this.products(1);
        },
        minRatingOption: function (value) {
            this.minRating = value;
            this.productSearchForm.min_rating = value;
            this.products(1);
        },
        trendModeOption: function (value) {
            this.trendMode = value;
            this.productSearchForm.trend_mode = value;
            this.products(1);
        },
        shippingCostModeOption: function (value) {
            this.shippingCostMode = value;
            this.productSearchForm.shipping_cost_mode = value;
            this.products(1);
        },
        refundableModeOption: function (value) {
            this.refundableMode = value;
            this.productSearchForm.refundable_mode = value;
            this.products(1);
        },
        wholesaleModeOption: function (value) {
            this.wholesaleMode = value;
            this.productSearchForm.wholesale_mode = value;
            this.products(1);
        },
        tagOption: function (tagName) {
            const index = this.selectedTagNames.indexOf(tagName);
            if (index >= 0) {
                this.selectedTagNames.splice(index, 1);
            } else {
                this.selectedTagNames.push(tagName);
            }

            this.productSearchForm.tag_names = this.selectedTagNames.length ? JSON.stringify(this.selectedTagNames) : null;
            this.products(1);
        },
        customFieldOption: function (event, key, value) {
            const normalizedKey = String(key);
            const normalizedValue = String(value);

            if (event.target.checked) {
                const exists = this.selectedCustomFields.some((item) => String(item.key) === normalizedKey && String(item.value) === normalizedValue);
                if (!exists) {
                    this.selectedCustomFields.push({
                        key: normalizedKey,
                        value: normalizedValue,
                    });
                }
            } else {
                this.selectedCustomFields = this.selectedCustomFields.filter((item) => !(String(item.key) === normalizedKey && String(item.value) === normalizedValue));
            }

            this.productSearchForm.custom_fields = this.selectedCustomFields.length ? JSON.stringify(this.selectedCustomFields) : null;
            this.products(1);
        },
    },
    watch: {
        $route() {
            this.ancestorsAndSelf();
        }
    }
}
</script>